import React, { useEffect, useState } from 'react';
import {
  Input,
  Checkbox,
  Switch,
  Dropdown,
  Option,
  Button,
  makeStyles,
  shorthands,
  tokens,
  Text,
} from '@fluentui/react-components';
import { ArrowUpload24Regular } from '@fluentui/react-icons';
import { apiFetch } from '../lib/apiFetch';
import { isSandbox } from '../config/appEnv';

// Human labels for the backend's effective email_delivery_mode.
const EMAIL_MODE_LABEL: Record<string, string> = {
  prod: 'Live (production)',
  live: 'Live — real recipients',
  redirect: 'Redirected to test inbox',
  suppressed: 'Suppressed (not sent)',
};

const useStyles = makeStyles({
  container: {
    display: 'flex',
    ...shorthands.gap('40px'),
    ...shorthands.padding('24px')
  },
  leftText: {
    width: '220px',
    flexShrink: 0,
  },
  textSection: {
    marginBottom: '48px',
  },
  textTitle: {
    fontSize: '16px',
    fontWeight: 600,
    marginBottom: '8px',
    color: '#000',
  },
  textDescription: {
    fontSize: '14px',
    color: '#605e5c',
    lineHeight: '1.5',
  },
  mainContent: {
    width: '620px',
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('24px'),
  },
  panel: {
    backgroundColor: '#ffffff',
    ...shorthands.borderRadius('8px'),
    boxShadow: tokens.shadow8,
    ...shorthands.padding('24px'),
  },
  customerName: {
    fontSize: '24px',
    fontWeight: 600,
    marginBottom: '24px',
  },
  section: {
    marginBottom: '0',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: 600,
    marginBottom: '16px',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    ...shorthands.gap('16px'),
    '@media (max-width: 768px)': {
      gridTemplateColumns: '1fr',
    },
  },
  formField: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('8px'),
    width: '100%',
    paddingBottom: '1rem'
  },
  Input: {
    width: '100%'
  },
  formFieldFull: {
    gridColumn: '1 / -1',
  },
  label: {
    fontSize: '14px',
    fontWeight: 600,
  },
  helperText: {
    fontSize: '12px',
    color: '#605e5c',
  },
  addressSection: {
    backgroundColor: '#f9f9f9',
    ...shorthands.padding('16px'),
    ...shorthands.borderRadius('4px'),
    marginTop: '16px',
  },
  addressTitle: {
    fontSize: '16px',
    fontWeight: 600,
    marginBottom: '16px',
  },
  depositOption: {
    ...shorthands.padding('12px'),
    ...shorthands.border('1px', 'solid', '#e0e0e0'),
    ...shorthands.borderRadius('4px'),
    marginBottom: '12px',
  },
  depositOptionTitle: {
    fontWeight: 600,
    marginBottom: '4px',
  },
  depositOptionDescription: {
    fontSize: '13px',
    color: '#605e5c',
  },
  actions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    ...shorthands.padding('16px', '0'),
  },
  actionButtons: {
    display: 'flex',
    gap: '12px',
  },
  securityDepositHeader: {
    fontSize: '14px',
    fontWeight: 600,
    marginBottom: '8px',
  },
  securityDepositSubtext: {
    fontSize: '14px',
    color: '#0078d4',
    marginBottom: '16px',
  },
  radioOption: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: '12px',
  },
  radioLabel: {
    fontWeight: 600,
    fontSize: '14px',
    marginBottom: '4px',
  },
  radioDescription: {
    fontSize: '13px',
    color: '#605e5c',
    marginLeft: '28px',
  },
  ordersTableContainer: {
    backgroundColor: '#ffffff',
    borderRadius: '4px',
    boxShadow: tokens.shadow8,
    ...shorthands.padding('16px'),
    marginTop: '16px',
  },
  ordersTable: {
    width: '100%',
  },
  orderRow: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  dateTimeCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  paginationFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: '8px',
    marginTop: '16px',
    fontSize: '14px',
    color: '#605e5c',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    ...shorthands.padding('80px', '24px'),
    minHeight: '300px',
  },
  emptyStateIcon: {
    fontSize: '64px',
    color: '#8a8886',
    marginBottom: '16px',
  },
  emptyStateText: {
    fontSize: '16px',
    color: '#605e5c',
  },
});

const SettingsGeneral: React.FC = () => {
  const styles = useStyles();
  const [companyName, setCompanyName] = useState('');
  const [email, setEmail] = useState('');                 // info@ — general contact
  const [billingEmail, setBillingEmail] = useState('');   // billing@ — shown on invoices
  const [supportEmail, setSupportEmail] = useState('');   // support@ — staff mailbox
  const [itEmail, setItEmail] = useState('');             // it@ — internal only
  const [website, setWebsite] = useState('');
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);
  const [country, setCountry] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [additionalInfoLine1, setAdditionalInfoLine1] = useState('');
  const [additionalInfoLine2, setAdditionalInfoLine2] = useState('');
  const [uei, setUei] = useState('');                         // federal UEI (gov invoices)
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [timeZone, setTimeZone] = useState('');
  const [firstWeekday, setFirstWeekday] = useState('');
  const [dateFormat, setDateFormat] = useState('');
  const [useAMPM, setUseAMPM] = useState(false);
  const [emailOverride, setEmailOverride] = useState(false);  // sandbox: send live
  const [emailMode, setEmailMode] = useState('');             // effective delivery mode
  const [systemBcc, setSystemBcc] = useState('');             // BCC'd on every send

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('setPageContext', {
      detail: { title: 'General' },
    }));
    return () => {
      window.dispatchEvent(new CustomEvent('setPageContext', { detail: null }));
    };
  }, []);

  // Load the singleton company record (contact + email aliases).
  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch('/company');
        if (!res.ok) return;
        const c = await res.json();
        setCompanyName(c.name ?? '');
        setEmail(c.email ?? '');
        setBillingEmail(c.billing_email ?? '');
        setSupportEmail(c.support_email ?? '');
        setItEmail(c.it_email ?? '');
        setPhone(c.phone ?? '');
        setWebsite(c.website ?? '');
        setCountry(c.country ?? '');
        setAddressLine1(c.address_line_1 ?? '');
        setAddressLine2(c.address_line_2 ?? '');
        setCity(c.city ?? '');
        setState(c.region ?? '');
        setZipCode(c.zipcode ?? '');
        setAdditionalInfoLine1(c.financial_line_1 ?? '');
        setAdditionalInfoLine2(c.financial_line_2 ?? '');
        setUei(c.uei ?? '');
        setTimeZone(c.default_timezone ?? '');
        setEmailOverride(!!c.sandbox_email_override);
        setEmailMode(c.email_delivery_mode ?? '');
        setSystemBcc(c.system_bcc ?? '');
      } catch {
        /* leave fields blank on failure */
      }
    })();
  }, []);

  // Persist a partial set of company fields. Each Save button passes only the
  // fields it owns; the backend whitelists and updates the singleton record.
  const save = async (fields: Record<string, unknown>) => {
    setSaving(true);
    setSaveMsg(null);
    try {
      const res = await apiFetch('/company', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(fields),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Save failed');
      setSaveMsg('Saved');
    } catch (e: any) {
      setSaveMsg(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const saveCompany = () => save({
    name: companyName,
    email,
    billing_email: billingEmail,
    support_email: supportEmail,
    it_email: itEmail,
    phone,
    website,
  });

  const saveAddress = () => save({
    country,
    address_line_1: addressLine1,
    address_line_2: addressLine2,
    city,
    region: state,
    zipcode: zipCode,
  });

  const saveAdditionalInfo = () => save({
    financial_line_1: additionalInfoLine1,
    financial_line_2: additionalInfoLine2,
    uei,
  });

  const saveLocalization = () => save({
    default_timezone: timeZone,
  });

  const saveSystemBcc = () => save({
    system_bcc: systemBcc,
  });

  // Sandbox-only: flip the live-send override, then re-read the effective mode
  // (redirect vs suppressed depends on backend env) and notify the header badge.
  const toggleEmailOverride = async (checked: boolean) => {
    setEmailOverride(checked);  // optimistic
    await save({ sandbox_email_override: checked });
    try {
      const res = await apiFetch('/company');
      if (res.ok) {
        const c = await res.json();
        setEmailOverride(!!c.sandbox_email_override);
        setEmailMode(c.email_delivery_mode ?? '');
        window.dispatchEvent(new CustomEvent('sandboxEmailModeChanged', {
          detail: { mode: c.email_delivery_mode ?? '' },
        }));
      }
    } catch {
      /* mode label just stays stale on a refresh failure */
    }
  };

  return (
    <div style={{ paddingTop: '16px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', padding: '24px' }}>

        <>
          {/* Sandbox email delivery (sandbox env only) */}
          {isSandbox() && (
            <div className={styles.container} style={{ padding: 0 }}>
              <div className={styles.mainContent}>
                <div className={styles.panel}>
                  <div className={styles.sectionTitle}>Sandbox email delivery</div>
                  <Text style={{ display: 'block', marginBottom: '16px', color: '#605e5c', fontSize: '13px' }}>
                    Sandbox normally suppresses or redirects all outbound email so test
                    sends never reach real inboxes. Turn this on to send live to the
                    actual recipients from the sandbox tenant.
                  </Text>
                  <Switch
                    checked={emailOverride}
                    label={emailOverride
                      ? 'Sending live to real recipients'
                      : 'Suppressed / redirected (default)'}
                    onChange={(_e, data) => toggleEmailOverride(data.checked === true)}
                  />
                  <div style={{ marginTop: '12px' }}>
                    <Text style={{ fontSize: '13px' }}>
                      Current mode:{' '}
                      <strong style={{ color: emailMode === 'live' ? '#ca5010' : undefined }}>
                        {EMAIL_MODE_LABEL[emailMode] ?? emailMode ?? '—'}
                      </strong>
                    </Text>
                  </div>
                </div>
              </div>
            </div>
          )}
          {/* System email BCC — applies in every env */}
          <div className={styles.container} style={{ padding: 0 }}>
            <div className={styles.mainContent}>
              <div className={styles.panel}>
                <div className={styles.sectionTitle}>System email BCC</div>
                <Text style={{ display: 'block', marginBottom: '16px', color: '#605e5c', fontSize: '13px' }}>
                  These addresses are blind-copied on every email the system sends to
                  customers — order confirmations, receipts, invoices, password resets,
                  everything. Separate multiple addresses with commas. Leave blank to
                  disable. Recipients never see these addresses.
                </Text>
                <div className={styles.formField}>
                  <label className={styles.label}>BCC addresses</label>
                  <Input
                    value={systemBcc}
                    onChange={(e, data) => setSystemBcc(data.value)}
                    placeholder="owner@example.com, zach@example.com"
                  />
                </div>
                <div className={styles.actionButtons}>
                  <Button appearance="primary" disabled={saving} onClick={saveSystemBcc}>
                    {saving ? 'Saving…' : 'Save'}
                  </Button>
                  {saveMsg ? <Text style={{ alignSelf: 'center' }}>{saveMsg}</Text> : null}
                </div>
              </div>
            </div>
          </div>
          {/* General Information Section */}
          <div className={styles.container} style={{ padding: 0 }}>
            <div className={styles.mainContent}>
              {/* General Information Panel */}
              <div className={styles.panel}>
                {/* Customer Name */}
                <div className={styles.formField}>
                  <label className={styles.label}>Company Name</label>
                  <Input value={companyName} onChange={(e, data) => setCompanyName(data.value)} />
                </div>
                <div className={styles.formField}>
                  <label className={styles.label}>Email (info@ — general contact)</label>
                  <Input value={email} onChange={(e, data) => setEmail(data.value)} placeholder="info@example.com" />
                </div>
                <div className={styles.formField}>
                  <label className={styles.label}>Billing email (billing@ — shown on invoices &amp; quotes)</label>
                  <Input value={billingEmail} onChange={(e, data) => setBillingEmail(data.value)} placeholder="billing@example.com" />
                </div>
                <div className={styles.formField}>
                  <label className={styles.label}>Support email (support@ — shared staff mailbox)</label>
                  <Input value={supportEmail} onChange={(e, data) => setSupportEmail(data.value)} placeholder="support@example.com" />
                </div>
                <div className={styles.formField}>
                  <label className={styles.label}>IT email (it@ — internal only, not customer-facing)</label>
                  <Input value={itEmail} onChange={(e, data) => setItEmail(data.value)} placeholder="it@example.com" />
                </div>
                <div className={styles.formField}>
                  <label className={styles.label}>Phone</label>
                  <Input value={phone} onChange={(e, data) => setPhone(data.value)} contentBefore={<span>🇨🇦</span>} />
                </div>
                <div className={styles.formField}>
                  <label className={styles.label}>Website</label>
                  <Input value={website} onChange={(e, data) => setWebsite(data.value)} placeholder="https://example.com" />
                </div>
                <div className={styles.formField}>
                  <label className={styles.label}>Logo</label>
                  <Button type="button" icon={<ArrowUpload24Regular />}>
                    Upload image
                  </Button>
                  <label>or drop Image</label>
                  <input accept="image/jpeg,.jpg,.jpeg,image/png,.png,image/webp,.webp" />
                </div>
                <div className={styles.actionButtons}>
                  <Button appearance="primary" disabled={saving} onClick={saveCompany}>
                    {saving ? 'Saving…' : 'Save'}
                  </Button>
                  {saveMsg ? <Text style={{ alignSelf: 'center' }}>{saveMsg}</Text> : null}
                </div>
              </div>
            </div>
          </div>
          {/* Addresses Section */}
          <div className={styles.container} style={{ padding: 0 }}>
            <div className={styles.mainContent}>
              <div className={styles.panel}>
                <div className={styles.section}>
                  <div className={styles.sectionTitle}>Addresses</div>
                  <div className={styles.formField} style={{ marginBottom: '16px' }}>
                    <label className={styles.label}>Country</label>
                    <Dropdown value={country} selectedOptions={[country]} onOptionSelect={(e, data) => setCountry(data.optionValue as string)}>
                      <Option value="Canada">Canada</Option>
                      <Option value="United States">United States</Option>
                      <Option value="Mexico">Mexico</Option>
                    </Dropdown>
                  </div>
                  <div className={styles.formField} style={{ marginBottom: '16px' }}>
                    <label className={styles.label}>Address line 1</label>
                    <Input
                      value={addressLine1}
                      onChange={(e, data) => setAddressLine1(data.value)}
                      placeholder="Address"
                    />
                  </div>

                  <div className={styles.formField} style={{ marginBottom: '16px' }}>
                    <label className={styles.label}>Address line 2</label>
                    <Input
                      value={addressLine2}
                      onChange={(e, data) => setAddressLine2(data.value)}
                      placeholder="Apartment, suite, etc. (optional)"
                    />
                  </div>
                  <div className={styles.formField}>
                    <label className={styles.label}>City</label>
                    <Input
                      value={city}
                      onChange={(e, data) => setCity(data.value)}
                      placeholder="City"
                    />
                  </div>
                  <div className={styles.formField}>
                    <label className={styles.label}>State</label>
                    <Dropdown placeholder="Select..." value={state} onOptionSelect={(e, data) => setState(data.optionValue as string)}>
                      <Option value="AB">Alberta</Option>
                      <Option value="BC">British Columbia</Option>
                      <Option value="MB">Manitoba</Option>
                      <Option value="NB">New Brunswick</Option>
                      <Option value="NL">Newfoundland and Labrador</Option>
                      <Option value="NS">Nova Scotia</Option>
                      <Option value="ON">Ontario</Option>
                      <Option value="PE">Prince Edward Island</Option>
                      <Option value="QC">Quebec</Option>
                      <Option value="SK">Saskatchewan</Option>
                    </Dropdown>
                  </div>
                  <div className={styles.formField}>
                    <label className={styles.label}>Zip code</label>
                    <Input value={zipCode} onChange={(e, data) => setZipCode(data.value)} placeholder="006383" />
                  </div>
                  <div className={styles.actionButtons}>
                    <Button appearance="primary" disabled={saving} onClick={saveAddress}>
                      {saving ? 'Saving…' : 'Save'}
                    </Button>
                    {saveMsg ? <Text style={{ alignSelf: 'center' }}>{saveMsg}</Text> : null}
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Additional Info Section */}
          <div className={styles.container} style={{ padding: 0 }}>
            <div className={styles.mainContent}>
              <div className={styles.panel}>
                <div className={styles.section}>
                  <Text style={{ marginBottom: '16px' }}>
                    Add any additional information you want your customers to see, like bank or tax information.
                  </Text>
                  <div className={styles.formField} style={{ marginBottom: '16px' }}>
                    <label className={styles.label}>Additional information line 1</label>
                    <Input value={additionalInfoLine1} onChange={(e, data) => setAdditionalInfoLine1(data.value)} placeholder="" />
                  </div>
                  <div className={styles.formField} style={{ marginBottom: '16px' }}>
                    <label className={styles.label}>Additional information line 2</label>
                    <Input value={additionalInfoLine2} onChange={(e, data) => setAdditionalInfoLine2(data.value)} placeholder="" />
                  </div>
                  <div className={styles.formField} style={{ marginBottom: '16px' }}>
                    <label className={styles.label}>UEI (Unique Entity Identifier)</label>
                    <Input value={uei} onChange={(e, data) => setUei(data.value)} placeholder="e.g. PK2PVHBCEWL5" />
                  </div>
                  <div className={styles.actionButtons}>
                    <Button appearance="primary" disabled={saving} onClick={saveAdditionalInfo}>
                      {saving ? 'Saving…' : 'Save'}
                    </Button>
                    {saveMsg ? <Text style={{ alignSelf: 'center' }}>{saveMsg}</Text> : null}
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Localization Section */}
          <div className={styles.container} style={{ padding: 0 }}>
            <div className={styles.mainContent}>
              <div className={styles.panel}>
                <div className={styles.section}>
                  <div className={styles.formField} style={{ marginBottom: '16px' }}>
                    <label className={styles.label}>Timezone</label>
                    <Dropdown placeholder="Select..." value={timeZone} onOptionSelect={(e, data) => setTimeZone(data.optionValue as string)}>
                      <Option value="UTC-12:00">UTC-12:00 — Baker Island Time</Option>
                      <Option value="UTC-11:00">UTC-11:00 — Niue Time</Option>
                      <Option value="UTC-10:00">UTC-10:00 — Hawaii-Aleutian Standard Time</Option>
                      <Option value="UTC-09:30">UTC-09:30 — Marquesas Time</Option>
                      <Option value="UTC-09:00">UTC-09:00 — Alaska Standard Time</Option>
                      <Option value="UTC-08:00">UTC-08:00 — Pacific Standard Time</Option>
                      <Option value="UTC-07:00">UTC-07:00 — Mountain Standard Time</Option>
                      <Option value="UTC-06:00">UTC-06:00 — Central Standard Time</Option>
                      <Option value="UTC-05:00">UTC-05:00 — Eastern Standard Time</Option>
                      <Option value="UTC-04:00">UTC-04:00 — Atlantic Standard Time</Option>
                      <Option value="UTC-03:30">UTC-03:30 — Newfoundland Standard Time</Option>
                      <Option value="UTC-03:00">UTC-03:00 — Argentina, Brazil, Uruguay</Option>
                      <Option value="UTC-02:00">UTC-02:00 — South Georgia Time</Option>
                      <Option value="UTC-01:00">UTC-01:00 — Azores Standard Time</Option>
                      <Option value="UTC+00:00">UTC+00:00 — Greenwich Mean Time</Option>
                      <Option value="UTC+01:00">UTC+01:00 — Central European Time</Option>
                      <Option value="UTC+02:00">UTC+02:00 — Eastern European Time</Option>
                      <Option value="UTC+03:00">UTC+03:00 — Moscow Standard Time</Option>
                      <Option value="UTC+03:30">UTC+03:30 — Iran Standard Time</Option>
                      <Option value="UTC+04:00">UTC+04:00 — Gulf Standard Time</Option>
                      <Option value="UTC+04:30">UTC+04:30 — Afghanistan Time</Option>
                      <Option value="UTC+05:00">UTC+05:00 — Pakistan Standard Time</Option>
                      <Option value="UTC+05:30">UTC+05:30 — India Standard Time</Option>
                      <Option value="UTC+05:45">UTC+05:45 — Nepal Time</Option>
                      <Option value="UTC+06:00">UTC+06:00 — Bangladesh Standard Time</Option>
                      <Option value="UTC+06:30">UTC+06:30 — Cocos Islands Time</Option>
                      <Option value="UTC+07:00">UTC+07:00 — Indochina Time</Option>
                      <Option value="UTC+08:00">UTC+08:00 — China Standard Time</Option>
                      <Option value="UTC+08:45">UTC+08:45 — Australian Central Western Time</Option>
                      <Option value="UTC+09:00">UTC+09:00 — Japan Standard Time</Option>
                      <Option value="UTC+09:30">UTC+09:30 — Australian Central Standard Time</Option>
                      <Option value="UTC+10:00">UTC+10:00 — Australian Eastern Standard Time</Option>
                      <Option value="UTC+10:30">UTC+10:30 — Lord Howe Standard Time</Option>
                      <Option value="UTC+11:00">UTC+11:00 — Solomon Islands Time</Option>
                      <Option value="UTC+12:00">UTC+12:00 — New Zealand Standard Time</Option>
                      <Option value="UTC+12:45">UTC+12:45 — Chatham Islands Time</Option>
                      <Option value="UTC+13:00">UTC+13:00 — Tonga Time</Option>
                      <Option value="UTC+14:00">UTC+14:00 — Line Islands Time</Option>
                    </Dropdown>
                  </div>
                  <div className={styles.formField} style={{ marginBottom: '16px' }}>
                    <label className={styles.label}>First day of the week</label>
                    <Dropdown placeholder="Select..." value={firstWeekday} onOptionSelect={(e, data) => setFirstWeekday(data.optionValue as string)}>
                      <Option value="Sunday">Sunday</Option>
                      <Option value="Monday">Monday</Option>
                      <Option value="Tuesday">Tuesday</Option>
                      <Option value="Wednesday">Wednesday</Option>
                      <Option value="Thursday">Thursday</Option>
                      <Option value="Friday">Friday</Option>
                      <Option value="Saturday">Saturday</Option>
                    </Dropdown>
                  </div>
                  <div className={styles.formField} style={{ marginBottom: '16px' }}>
                    <label className={styles.label}>Date format</label>
                    <Dropdown placeholder="Select..." value={dateFormat} onOptionSelect={(e, data) => setDateFormat(data.optionValue as string)}>
                      <Option value="dd-mm-yyyy">dd-mm-yyyy</Option>
                      <Option value="mm-dd-yyyy">mm-dd-yyyy</Option>
                      <Option value="yyyy-mm-dd">yyyy-mm-dd</Option>
                    </Dropdown>
                  </div>
                  <div style={{ marginTop: '8px', marginLeft: '-2px' }}>
                    <Checkbox label="Use AM/PM" checked={useAMPM} onChange={(e, data) => setUseAMPM(data.checked === true)} />
                  </div>
                  <div className={styles.actionButtons}>
                    <Button appearance="primary" disabled={saving} onClick={saveLocalization}>
                      {saving ? 'Saving…' : 'Save'}
                    </Button>
                    {saveMsg ? <Text style={{ alignSelf: 'center' }}>{saveMsg}</Text> : null}
                  </div>
                  <div className={styles.formField} style={{ marginBottom: '16px' }}>
                    <p><strong>Units of measurement</strong></p>
                    <p>
                      Used for calculating delivery fees and more
                    </p>
                    <p>[ ADD RADIO GROUP ]</p>
                    <p>Metric</p>
                    <p>Imperial</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      </div>
    </div>
  );
};

export default SettingsGeneral;